import { useState, type FormEvent } from "react";
import { useHistory } from "react-router-dom";
import { Loader2, Search } from "lucide-react";
import api from "../../services/api";
import { useAuth } from "../../hooks/auth";
import refreshToken from "../../utils/refreshToken";
import { resolvePropertyNavigationId } from "../../utils/resolvePropertyNavigationId";
import { FLOW_TEXT_INPUT_BASE } from "../../styles/twDesignPrimitives";

/** "Buscar" next to the search inputs: Enter is not obvious, especially on mobile. */
export const SEARCH_SUBMIT_BUTTON =
  "inline-flex h-[38px] shrink-0 items-center justify-center gap-1.5 rounded-md border-0 bg-orange-500 px-3 text-sm font-semibold text-white transition hover:bg-orange-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 disabled:cursor-not-allowed disabled:opacity-50";

export function PropertiesCodeSearchField() {
  const history = useHistory();
  const { access_token, dateToExpires } = useAuth();
  const [code, setCode] = useState("");
  const [resolving, setResolving] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;

    const requestedId = Number(trimmed);
    if (!Number.isFinite(requestedId) || requestedId <= 0) {
      history.push(`/property/${trimmed}`);
      return;
    }

    setResolving(true);
    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;
      const targetId = await resolvePropertyNavigationId(api, requestedId);
      history.push(`/property/${targetId}`);
    } catch {
      history.push(`/property/${trimmed}`);
    } finally {
      setResolving(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      role="search"
      className="flex w-full min-w-0 shrink-0 flex-col gap-1 text-left text-xs font-medium text-slate-600 md:w-[260px]"
    >
      <label htmlFor="property-code-search">Código do imóvel</label>
      <div className="flex w-full gap-2">
        <input
          id="property-code-search"
          className={`${FLOW_TEXT_INPUT_BASE} h-[38px] min-w-0 flex-1 !font-[inherit]`}
          autoComplete="off"
          inputMode="numeric"
          enterKeyHint="go"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          type="text"
          placeholder="Ex: 55744"
          disabled={resolving}
        />
        <button type="submit" disabled={resolving || !code.trim()} className={SEARCH_SUBMIT_BUTTON}>
          {resolving ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Search className="h-4 w-4" aria-hidden />
          )}
          Buscar
        </button>
      </div>
    </form>
  );
}
